import { Stack } from '@mui/material'
import { ReactElement, useEffect, useState } from 'react'
import { Form } from 'react-final-form'
import { useParams } from 'react-router'
import { Kap_Goal, Kap_Goal_Final_Report_Insert_Input, Mutation_Root, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { queryMilestoneById } from 'src/screens/shared/implementation/details/milestoneQueries'
import { usePermissionsForKapMilestones } from 'src/service/security/PermissionHook'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { GOAL_CHAR, TEXT_LENGTH } from 'src/shared/constants/constants'
import {
  KAP_GOAL_FINAL_REPORT_RATING,
  MILESTONE_RESPONSIBLE_TYPE_TYPE,
  MILESTONE_STATUS_TYPE,
} from 'src/shared/constants/milestone-constants'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { HtmlEditorField } from 'src/shared/form/control/HtmlEditorField'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import { createDecorators } from 'src/shared/form/utils/decorators'
import { composeValidators, maxChar, required } from 'src/shared/form/validation/validators'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient, useQuery } from 'urql'
import { queryKapGoalFinalReport, upsertKapGoalFinalReportMutation } from '../kapMilestoneGoalFinalReportQueries'

interface GoalRatingForm {
  rating: string
  reason: string
}

const decorators = createDecorators()
const requiredHtml = (value: string) =>
  HtmlSanitizer.stripHtmlTags(value ?? '').trim() ? undefined : { errorKey: 'validation.required' }

export const KapMilestoneGoalFinalReportEditPage = (): ReactElement => {
  const { programId, milestoneId, milestonePath, goalId } = useParams()

  const parsedProgramId = parseInt(programId as string)
  const parsedMilestoneId = parseInt(milestoneId as string)
  const parsedGoalId = parseInt(goalId as string)

  const { getMessage } = useMessageSource()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()
  const urqlClient = useClient()

  const [initialValues, setInitialValues] = useState<GoalRatingForm>({
    rating: '',
    reason: '',
  })
  const [goal, setGoal] = useState<Kap_Goal>()
  const [goalLoading, setGoalLoading] = useState<boolean>(true)

  const goalTitle = goal ? `${goal.name} (${getMessage('label.goal.id.character')}${goal.sort_number})` : ''
  const ratingOptions = Object.keys(KAP_GOAL_FINAL_REPORT_RATING).map((key) => ({
    label: getMessage(`label.goal.rating.${key}`),
    value: key,
  }))

  const [{ data: milestoneData, error }] = useQuery<{ milestone_by_pk: Query_Root['milestone_by_pk'] }, { id: number }>(
    {
      query: queryMilestoneById,
      variables: { id: parsedMilestoneId },
    },
  )

  if (error) {
    notificationService.operationFailed()
  }

  useEffect(() => {
    const fetchGoal = async () => {
      setGoalLoading(true)
      const { data, error } = await urqlClient
        .query<{ kap_goal_by_pk: Query_Root['kap_goal_by_pk'] }, { goalId: number; milestoneId: number }>(
          queryKapGoalFinalReport,
          { goalId: parsedGoalId, milestoneId: parsedMilestoneId },
        )
        .toPromise()

      if (error) {
        notificationService.operationFailed()
      }

      const goal = data?.kap_goal_by_pk ?? undefined

      setGoal(goal)
      setInitialValues({
        rating: goal?.kap_goal_final_reports?.[0]?.rating ?? '',
        reason: goal?.kap_goal_final_reports?.[0]?.reason ?? '',
      })
      setGoalLoading(false)
    }

    fetchGoal()
  }, [notificationService, parsedGoalId, parsedMilestoneId, urqlClient])

  const milestone = milestoneData?.milestone_by_pk
  const { loading: permissionsLoading, canEdit } = usePermissionsForKapMilestones(
    parsedProgramId,
    milestone?.status as MILESTONE_STATUS_TYPE,
    milestone?.responsible_type as MILESTONE_RESPONSIBLE_TYPE_TYPE,
  )

  const loading = goalLoading || permissionsLoading

  const onBack = () => {
    const route = ROUTES.KapFinalReportMilestoneRoot.nested.GoalRating.params({
      programId,
      milestoneId,
      milestonePath,
    })
    navigate(`${route}#${GOAL_CHAR}${parsedGoalId}`, { state: { expandedEntityId: `${GOAL_CHAR}${parsedGoalId}` } })
  }

  let submit: any = () => {}
  let formValid = false

  const onSave = (event: any) => {
    submit(event)
  }

  const onSaveAndBack = async (event: any) => {
    try {
      await submit(event)
      formValid && onBack()
    } catch (error) {
      // do nothing
    }
  }

  const onSubmit = async (values: GoalRatingForm) => {
    const object: Kap_Goal_Final_Report_Insert_Input = {
      milestone_id: parsedMilestoneId,
      kap_goal_id: parsedGoalId,
      rating: values.rating,
      reason: HtmlSanitizer.sanitize(values.reason),
    }
    const { data } = await urqlClient
      .mutation<
        { insert_kap_goal_final_report_one: Mutation_Root['insert_kap_goal_final_report_one'] },
        { object: Kap_Goal_Final_Report_Insert_Input }
      >(upsertKapGoalFinalReportMutation, { object })
      .toPromise()

    if (data?.insert_kap_goal_final_report_one?.id) {
      notificationService.changesSaved()
      setInitialValues(values)
    } else {
      notificationService.operationFailed()
    }
  }

  if (!loading && !canEdit) {
    return <NotAuthorized inEditPage />
  }

  return (
    <>
      <ScreenLayout
        title={getMessage('label.edit.goal.milestone.title', [goalTitle])}
        onBack={onBack}
        hasSecondLevelNavigation={false}
        actions={
          <>
            <SaveAndBackButton origin="header" onClick={onSaveAndBack} />
            <SaveButton origin="header" onClick={onSave} />
          </>
        }
      >
        <PageLayout>
          <>
            {!loading && goal && (
              <>
                <HelpAndInstructions labelKey={`label.help.milestone.final.report.goal.rating.kap`} defaultExpansion />
                <Form<GoalRatingForm>
                  initialValues={initialValues}
                  onSubmit={onSubmit}
                  decorators={decorators}
                  render={({ handleSubmit, valid }) => {
                    submit = handleSubmit
                    formValid = valid
                    return (
                      <form onSubmit={handleSubmit} noValidate>
                        <Stack spacing={2}>
                          <AutoCompleteField
                            name="rating"
                            label={getMessage('label.milestone.final.report.goal.rating.kap')}
                            options={ratingOptions}
                            validate={required()}
                            required
                          />
                          <HtmlEditorField
                            name="reason"
                            label={getMessage('label.goal.rating.comment')}
                            validate={composeValidators(
                              requiredHtml,
                              maxChar(TEXT_LENGTH.KAP_GOAL_FINAL_REPORT_REASON),
                            )}
                            required
                          />
                          <DirtyFormSpy />
                        </Stack>
                      </form>
                    )
                  }}
                />
              </>
            )}
          </>
        </PageLayout>
      </ScreenLayout>
    </>
  )
}
